import { SlideRenderer } from "@qassist/slide-renderer";
import { useEffect, useRef, useState, type ComponentProps, type ReactElement } from "react";

export interface PreviewViewport {
  width: number;
  height: number;
}

type ScaledSlideRendererProps = ComponentProps<typeof SlideRenderer> & {
  viewport?: PreviewViewport;
};

export function ScaledSlideRenderer({
  viewport,
  ...rendererProps
}: ScaledSlideRendererProps): ReactElement {
  const { definition } = rendererProps;
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number | null>(null);
  const canvasWidth = viewport?.width ?? definition.canvas.width;
  const canvasHeight = viewport?.height ?? definition.canvas.height;

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const updateScale = (): void => {
      if (frame.clientWidth <= 0 || canvasWidth <= 0) return;
      setScale(frame.clientWidth / canvasWidth);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [canvasHeight, canvasWidth]);

  return (
    <div
      ref={frameRef}
      className="scaled-slide-preview"
      style={{ aspectRatio: `${canvasWidth} / ${canvasHeight}` }}
    >
      <div
        className="scaled-slide-preview__canvas"
        style={{
          width: canvasWidth,
          height: canvasHeight,
          opacity: scale === null ? 0 : 1,
          transform: `scale(${scale ?? 1})`,
        }}
      >
        <SlideRenderer {...rendererProps} />
      </div>
    </div>
  );
}
